import { PostHog } from 'posthog-js';
import { getConfig } from '../api/auth';
import { ITeacherProfile } from '../types/user';
import { userCache } from './user-cache';

export let posthog: PostHog | null = null;

//Todo: fix this for passwordless local env
const isPasswordless = process.env.NX_PUBLIC_ENV === 'passwordlessLocal';

const getFinalPosthogKey = (): string | null => {
  const selectedRegionLocal = userCache.getRegionFromCache();
  const finalPosthogKey =
    selectedRegionLocal?.analytics ?? process.env.NX_PUBLIC_POSTHOG_KEY;
  return finalPosthogKey ?? null;
};

const initializePosthogInstance = (apiKey: string) => {
  if (posthog) {
    posthog.reset();
  }
  posthog = new PostHog();
  posthog.init(apiKey, {
    api_host: process.env.NX_PUBLIC_POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: true,
  });
};

export const posthogInit = () => {
  if (isPasswordless) {
    return;
  }
  const finalPosthogKey = getFinalPosthogKey();
  if (finalPosthogKey) {
    initializePosthogInstance(finalPosthogKey);
  }
};

const identify = () => {
  if (!posthog) return;
  const user = userCache.getUserFromCache();
  const selectedProfile = userCache.getActiveProfile() as ITeacherProfile;
  const config = getConfig();
  if (!user || !selectedProfile || !config) {
    return;
  }

  posthog.identify(user.username, {
    role: selectedProfile.role,
    profile_id: selectedProfile.id,
    joining_date: selectedProfile.joiningDate,
    school: selectedProfile.school,
    years_of_experience: selectedProfile.yearsOfService,
    organization: config.app,
    user_id: user.id,
    emis: selectedProfile?.emis,
  });

  // Session recording is controlled by backend-provided feature flags (user.featureFlags),
  // NOT PostHog's client-side isFeatureEnabled(). We use the synchronous backend check
  // because PostHog's async onFeatureFlags()/isFeatureEnabled() is unreliable here —
  // the /decide/ endpoint may evaluate the flag before identify()'s person properties
  // are stored server-side, causing it to always return false.
  //
  // The backend already targets only specific mobile numbers for this flag,
  // so this approach is safe for our recording quota.
  const isSessionReplayEnabled =
    user.featureFlags?.['session-replay-enabled'] === true ||
    user.featureFlags?.['sessionReplayEnabled'] === true;

  if (isSessionReplayEnabled) {
    // { linked_flag: true } is required to override PostHog's internal linked flag
    // check on the session recording module. Without this override,
    // disable_session_recording: true in the init config causes PostHog to block
    // startSessionRecording() even when we've determined the user should be recorded.
    posthog.startSessionRecording({ linked_flag: true });
  } else {
    posthog.stopSessionRecording();
  }
};

export const posthogIdentify = async () => {
  if (isPasswordless) {
    return;
  }
  if (!posthog) {
    posthogInit();
  }
  identify();
};
